import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, EditIcon, MailIcon, PhoneIcon, MapPinIcon, CreditCardIcon, FileTextIcon, ActivityIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { Customer, PaymentMethod, CustomerTransaction } from '../types/Customer';
export function CustomerDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [activeTab, setActiveTab] = useState<'invoices' | 'cards' | 'transactions'>('invoices');
  // Mock data - in a real app, this would be fetched from an API
  const customer: Customer = {
    id: id || '1',
    name: 'Acme Corporation',
    email: 'billing@acme.com',
    phone: '+94 77 123 4567',
    address: '123 Business Street',
    city: 'Colombo',
    country: 'Sri Lanka',
    createdAt: '2023-06-15',
    totalSpent: 875000,
    invoiceCount: 12,
    status: 'active'
  };
  const paymentMethods: PaymentMethod[] = [{
    id: '1',
    type: 'visa',
    last4: '4242',
    expiryMonth: 12,
    expiryYear: 2025,
    cardholderName: 'John Doe',
    isDefault: true,
    addedAt: '2023-06-15'
  }, {
    id: '2',
    type: 'mastercard',
    last4: '5555',
    expiryMonth: 8,
    expiryYear: 2026,
    cardholderName: 'John Doe',
    isDefault: false,
    addedAt: '2023-09-20'
  }];
  const customerInvoices = [{
    id: '1',
    invoiceNumber: 'INV-2024-001',
    date: '2024-01-01',
    amount: 125000,
    status: 'paid' as const,
    dueDate: '2024-01-15'
  }, {
    id: '2',
    invoiceNumber: 'INV-2024-015',
    date: '2024-01-15',
    amount: 87500,
    status: 'sent' as const,
    dueDate: '2024-02-01'
  }, {
    id: '3',
    invoiceNumber: 'INV-2023-089',
    date: '2023-12-01',
    amount: 150000,
    status: 'paid' as const,
    dueDate: '2023-12-15'
  }];
  const transactions: CustomerTransaction[] = [{
    id: '1',
    date: '2024-01-10',
    description: 'Invoice Payment - INV-2024-001',
    amount: 125000,
    status: 'completed',
    paymentMethod: 'Visa •••• 4242',
    invoiceId: '1'
  }, {
    id: '2',
    date: '2023-12-10',
    description: 'Invoice Payment - INV-2023-089',
    amount: 150000,
    status: 'completed',
    paymentMethod: 'Visa •••• 4242',
    invoiceId: '3'
  }, {
    id: '3',
    date: '2023-11-15',
    description: 'Invoice Payment - INV-2023-078',
    amount: 95000,
    status: 'completed',
    paymentMethod: 'Mastercard •••• 5555'
  }, {
    id: '4',
    date: '2023-11-01',
    description: 'Refund - INV-2023-065',
    amount: -25000,
    status: 'refunded',
    paymentMethod: 'Visa •••• 4242'
  }];
  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-600',
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status as keyof typeof styles]}`} data-id="element-3594">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const getCardIcon = (type: string) => {
    return <CreditCardIcon size={20} data-id="element-3595" />;
  };
  return <div className="space-y-6" data-id="element-3596">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-3597">
        <button onClick={() => navigate('/customers')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-3598">
          <ArrowLeftIcon size={20} data-id="element-3599" />
          Back to Customers
        </button>
        <button onClick={() => navigate(`/customer-form?id=${customer.id}`)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-3600">
          <EditIcon size={18} data-id="element-3601" />
          Edit Customer
        </button>
      </div>
      {/* Customer Info Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-3602">
        <div className="flex items-start justify-between" data-id="element-3603">
          <div className="flex items-start gap-4" data-id="element-3604">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-xl" data-id="element-3605">
              {customer.name.charAt(0)}
            </div>
            <div data-id="element-3606">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-3607">
                {customer.name}
              </h1>
              <div className="mt-2 space-y-1" data-id="element-3608">
                <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-3609">
                  <MailIcon size={16} data-id="element-3610" />
                  {customer.email}
                </div>
                {customer.phone && <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-3611">
                    <PhoneIcon size={16} data-id="element-3612" />
                    {customer.phone}
                  </div>}
                <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-3613">
                  <MapPinIcon size={16} data-id="element-3614" />
                  {customer.address}, {customer.city}, {customer.country}
                </div>
              </div>
            </div>
          </div>
          <div className="text-right" data-id="element-3615">
            {getStatusBadge(customer.status)}
            <div className="mt-2 text-sm text-gray-500" data-id="element-3616">
              Customer since{' '}
              {new Date(customer.createdAt).toLocaleDateString('en-GB')}
            </div>
          </div>
        </div>
        {/* Stats */}
        <div className="mt-6 pt-6 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3617">
          <div data-id="element-3618">
            <div className="text-sm text-gray-500" data-id="element-3619">Total Spent</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-3620">
              Rs. {customer.totalSpent.toLocaleString('en-LK')}
            </div>
          </div>
          <div data-id="element-3621">
            <div className="text-sm text-gray-500" data-id="element-3622">Total Invoices</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-3623">
              {customer.invoiceCount}
            </div>
          </div>
          <div data-id="element-3624">
            <div className="text-sm text-gray-500" data-id="element-3625">Avg. Invoice Value</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-3626">
              Rs.{' '}
              {Math.round(customer.totalSpent / customer.invoiceCount).toLocaleString('en-LK')}
            </div>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-3627">
        <div className="border-b border-gray-200" data-id="element-3628">
          <nav className="flex -mb-px" data-id="element-3629">
            <button onClick={() => setActiveTab('invoices')} className={`px-6 py-4 text-sm font-medium border-b-2 ${activeTab === 'invoices' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3630">
              <div className="flex items-center gap-2" data-id="element-3631">
                <FileTextIcon size={18} data-id="element-3632" />
                Invoices
              </div>
            </button>
            <button onClick={() => setActiveTab('cards')} className={`px-6 py-4 text-sm font-medium border-b-2 ${activeTab === 'cards' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3633">
              <div className="flex items-center gap-2" data-id="element-3634">
                <CreditCardIcon size={18} data-id="element-3635" />
                Payment Methods
              </div>
            </button>
            <button onClick={() => setActiveTab('transactions')} className={`px-6 py-4 text-sm font-medium border-b-2 ${activeTab === 'transactions' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3636">
              <div className="flex items-center gap-2" data-id="element-3637">
                <ActivityIcon size={18} data-id="element-3638" />
                Transactions
              </div>
            </button>
          </nav>
        </div>
        <div className="p-6" data-id="element-3639">
          {/* Invoices Tab */}
          {activeTab === 'invoices' && <div data-id="element-3640">
              <div className="overflow-x-auto" data-id="element-3641">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-3642">
                  <thead data-id="element-3643">
                    <tr data-id="element-3644">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3645">
                        Invoice
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3646">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3647">
                        Due Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3648">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3649">
                        Status
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-3650">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-3651">
                    {customerInvoices.map(invoice => <tr key={invoice.id} className="hover:bg-gray-50" data-id="element-3652">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900" data-id="element-3653">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500" data-id="element-3654">
                          {new Date(invoice.date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500" data-id="element-3655">
                          {new Date(invoice.dueDate).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900" data-id="element-3656">
                          Rs. {invoice.amount.toLocaleString('en-LK')}
                        </td>
                        <td className="px-4 py-4 text-sm" data-id="element-3657">
                          {getStatusBadge(invoice.status)}
                        </td>
                        <td className="px-4 py-4 text-sm text-right" data-id="element-3658">
                          <button onClick={() => navigate(`/invoice-detail/${invoice.id}`)} className="text-blue-600 hover:text-blue-900" data-id="element-3659">
                            View
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
          {/* Payment Methods Tab */}
          {activeTab === 'cards' && <div className="space-y-4" data-id="element-3660">
              <div className="flex justify-between items-center mb-4" data-id="element-3661">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-3662">
                  Stored Payment Methods
                </h3>
                <button className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg" data-id="element-3663">
                  <PlusIcon size={18} data-id="element-3664" />
                  Add Card
                </button>
              </div>
              {paymentMethods.map(method => <div key={method.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-3665">
                  <div className="flex items-center justify-between" data-id="element-3666">
                    <div className="flex items-center gap-4" data-id="element-3667">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center" data-id="element-3668">
                        {getCardIcon(method.type)}
                      </div>
                      <div data-id="element-3669">
                        <div className="flex items-center gap-2" data-id="element-3670">
                          <span className="text-sm font-medium text-gray-900" data-id="element-3671">
                            {method.type.charAt(0).toUpperCase() + method.type.slice(1)}{' '}
                            •••• {method.last4}
                          </span>
                          {method.isDefault && <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded" data-id="element-3672">
                              Default
                            </span>}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-3673">
                          Expires {method.expiryMonth}/{method.expiryYear}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-3674">
                          {method.cardholderName}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-3675">
                      {!method.isDefault && <button className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded" data-id="element-3676">
                          Set as Default
                        </button>}
                      <button className="p-2 text-red-600 hover:bg-red-50 rounded" data-id="element-3677">
                        <TrashIcon size={18} data-id="element-3678" />
                      </button>
                    </div>
                  </div>
                </div>)}
              {paymentMethods.length === 0 && <div className="text-center py-8 text-gray-500" data-id="element-3679">
                  No payment methods stored
                </div>}
            </div>}
          {/* Transactions Tab */}
          {activeTab === 'transactions' && <div data-id="element-3680">
              <div className="overflow-x-auto" data-id="element-3681">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-3682">
                  <thead data-id="element-3683">
                    <tr data-id="element-3684">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3685">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3686">
                        Description
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3687">
                        Payment Method
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3688">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3689">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-3690">
                    {transactions.map(transaction => <tr key={transaction.id} className="hover:bg-gray-50" data-id="element-3691">
                        <td className="px-4 py-4 text-sm text-gray-500" data-id="element-3692">
                          {new Date(transaction.date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900" data-id="element-3693">
                          {transaction.description}
                          {transaction.invoiceId && <button onClick={() => navigate(`/invoice-detail/${transaction.invoiceId}`)} className="ml-2 text-blue-600 hover:text-blue-900" data-id="element-3694">
                              View Invoice
                            </button>}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500" data-id="element-3695">
                          {transaction.paymentMethod || '-'}
                        </td>
                        <td className="px-4 py-4 text-sm" data-id="element-3696">
                          <span className={transaction.amount < 0 ? 'text-red-600' : 'text-gray-900'} data-id="element-3697">
                            {transaction.amount < 0 ? '-' : ''}Rs.{' '}
                            {Math.abs(transaction.amount).toLocaleString('en-LK')}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm" data-id="element-3698">
                          {getStatusBadge(transaction.status)}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}